import React, { useMemo, useState } from 'react';
import { Wallet, Copy, Check, Download, Calendar, Shield, Info, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  completed: { label: 'Paid', className: 'bg-emerald-100 text-emerald-700' },
  processing: { label: 'Processing', className: 'bg-blue-100 text-blue-700' },
  pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700' },
  failed: { label: 'Failed', className: 'bg-red-100 text-red-700' },
};

const TRIGGER_META = {
  scheduler: { label: 'Automatic', tooltip: 'Weekly cron, Monday 04:00 UTC' },
  admin: { label: 'Admin', tooltip: 'Triggered manually by platform admin' },
  manual: { label: 'Admin', tooltip: 'Triggered manually by platform admin' },
};

function fmtDate(iso) {
  if (!iso) return ',';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return ','; }
}

function fmtDateTime(iso) {
  if (!iso) return ',';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return ','; }
}

function fmtNextPayout(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const day = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
    const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
    return `${day} · ${time} UTC`;
  } catch { return null; }
}

function toCsv(payouts) {
  const header = ['Payout ID', 'Reference', 'Date', 'Processed at', 'Amount (£)', 'Status', 'Method', 'Triggered by'];
  const rows = payouts.map((p) => [
    p.payout_id,
    p.reference || '',
    p.created_at || '',
    p.processed_at || '',
    Number(p.amount || 0).toFixed(2),
    p.status || '',
    p.payout_method || '',
    p.triggered_by || '',
  ]);
  const escape = (v) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [header, ...rows].map((row) => row.map(escape).join(',')).join('\n');
}

export function PayoutHistory({ payouts, payoutThreshold, nextPayoutAt }) {
  const [copiedRef, setCopiedRef] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const { totalPaid, lastPayoutDate } = useMemo(() => {
    const paid = (payouts || []).filter((p) => p.status === 'completed' || p.status === 'processing');
    return {
      totalPaid: paid.reduce((sum, p) => sum + (p.amount || 0), 0),
      lastPayoutDate: paid[0]?.created_at || null,
    };
  }, [payouts]);

  const handleCopy = async (ref) => {
    try {
      await navigator.clipboard.writeText(ref);
      setCopiedRef(ref);
      toast.success('Reference copied');
      setTimeout(() => setCopiedRef(null), 1800);
    } catch {
      toast.error('Unable to copy');
    }
  };

  const handleExport = () => {
    if (!payouts.length) return;
    const csv = toCsv(payouts);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `zuriya_payout_history_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toast.success('Payout history exported');
  };

  const nextPayoutLabel = fmtNextPayout(nextPayoutAt);

  return (
    <div className="card-surface p-6" data-testid="payout-history">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-medium text-[#1A1A1A] flex items-center gap-2">
            <Wallet className="h-4 w-4 text-[#D4AF37]" />
            Payout History
          </h3>
          <p className="text-xs text-stone-400 mt-1">
            Automatic weekly deposits to your registered bank account. Cleared 48h after service completion.
          </p>
        </div>
        {payouts.length > 0 && (
          <button
            onClick={handleExport}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1A1A1A] hover:text-[#D4AF37] transition-colors border border-[#E6E4DD] rounded-md px-3 py-1.5"
            data-testid="export-payouts-csv-btn"
          >
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </button>
        )}
      </div>

      {payouts.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4" data-testid="payout-summary-row">
          <div className="rounded-lg border border-[#E6E4DD] bg-[#F9F6F0]/60 px-3 py-2.5">
            <p className="text-[11px] uppercase tracking-wide text-stone-400">Total paid out</p>
            <p className="text-lg font-semibold text-[#1A1A1A]" data-testid="payout-summary-total">
              £{totalPaid.toFixed(2)}
            </p>
          </div>
          <div className="rounded-lg border border-[#E6E4DD] bg-[#F9F6F0]/60 px-3 py-2.5">
            <p className="text-[11px] uppercase tracking-wide text-stone-400">Payouts received</p>
            <p className="text-lg font-semibold text-[#1A1A1A]" data-testid="payout-summary-count">
              {payouts.length}
            </p>
          </div>
          <div className="rounded-lg border border-[#E6E4DD] bg-[#F9F6F0]/60 px-3 py-2.5">
            <p className="text-[11px] uppercase tracking-wide text-stone-400">Last payout</p>
            <p className="text-sm font-medium text-[#1A1A1A]" data-testid="payout-summary-last">
              {lastPayoutDate ? fmtDate(lastPayoutDate) : ','}
            </p>
          </div>
        </div>
      )}

      {payouts.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#E6E4DD] bg-[#F9F6F0]/40 px-4 py-8 text-center" data-testid="payout-history-empty">
          <CalendarClock className="h-6 w-6 text-[#D4AF37] mx-auto mb-2" />
          <p className="text-sm text-stone-600">No payouts yet.</p>
          <p className="text-xs text-stone-400 mt-1">
            Payouts run automatically every Monday at 04:00 UTC once your available balance reaches £{payoutThreshold.toFixed(0)}.
          </p>
          {nextPayoutLabel && (
            <p className="text-xs text-stone-500 mt-3 inline-flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              Next scheduled: <span className="font-medium text-[#1A1A1A]">{nextPayoutLabel}</span>
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E6E4DD]">
                <th className="text-left py-2 px-3 text-stone-500 font-medium">Date</th>
                <th className="text-left py-2 px-3 text-stone-500 font-medium">Reference</th>
                <th className="text-left py-2 px-3 text-stone-500 font-medium">Method</th>
                <th className="text-right py-2 px-3 text-stone-500 font-medium">Amount</th>
                <th className="text-left py-2 px-3 text-stone-500 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((payout) => {
                const status = STATUS_META[payout.status] || STATUS_META.pending;
                const trigger = TRIGGER_META[payout.triggered_by] || null;
                const reference = payout.reference || payout.payout_id;
                const isOpen = expanded === payout.payout_id;
                return (
                  <React.Fragment key={payout.payout_id}>
                    <tr
                      className="border-b border-[#E6E4DD] hover:bg-[#F9F6F0] cursor-pointer"
                      data-testid={`payout-row-${payout.payout_id}`}
                      onClick={() => setExpanded(isOpen ? null : payout.payout_id)}
                    >
                      <td className="py-3 px-3 text-stone-600 whitespace-nowrap">
                        {fmtDate(payout.created_at)}
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-xs text-stone-600" data-testid="payout-reference">
                            {reference}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCopy(reference); }}
                            className="opacity-60 hover:opacity-100 transition-opacity"
                            aria-label="Copy reference"
                            data-testid={`copy-payout-ref-${payout.payout_id}`}
                          >
                            {copiedRef === reference
                              ? <Check className="h-3.5 w-3.5 text-emerald-600" />
                              : <Copy className="h-3.5 w-3.5 text-stone-400" />}
                          </button>
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-stone-600 capitalize">
                            {(payout.payout_method || 'bank_transfer').replace(/_/g, ' ')}
                          </span>
                          {trigger && (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-500"
                              title={trigger.tooltip}
                            >
                              {trigger.label}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-3 text-right font-medium text-[#1A1A1A]">
                        £{Number(payout.amount || 0).toFixed(2)}
                      </td>
                      <td className="py-3 px-3">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.className}`}
                          data-testid={`payout-status-${payout.payout_id}`}
                        >
                          {status.label}
                        </span>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-[#F9F6F0]/60 border-b border-[#E6E4DD]" data-testid={`payout-details-${payout.payout_id}`}>
                        <td colSpan={5} className="py-3 px-3">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                            <div>
                              <p className="text-stone-400 uppercase tracking-wide">Payout ID</p>
                              <p className="font-mono text-stone-700 break-all">{payout.payout_id}</p>
                            </div>
                            <div>
                              <p className="text-stone-400 uppercase tracking-wide">Created</p>
                              <p className="text-stone-700">{fmtDateTime(payout.created_at)}</p>
                            </div>
                            <div>
                              <p className="text-stone-400 uppercase tracking-wide">Processed</p>
                              <p className="text-stone-700">{fmtDateTime(payout.processed_at)}</p>
                            </div>
                            {payout.failure_reason && (
                              <div className="sm:col-span-3">
                                <p className="text-red-500 uppercase tracking-wide">Failure reason</p>
                                <p className="text-red-700">{payout.failure_reason}</p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          <p className="text-[11px] text-stone-400 mt-3 inline-flex items-center gap-1">
            <Info className="h-3 w-3" />
            Bank transfers typically arrive within 1–2 working days of the payout date.
          </p>
        </div>
      )}

      <div className="mt-4 flex items-start gap-2 rounded-lg bg-[#F9F6F0] border border-[#E6E4DD] px-3 py-2">
        <Shield className="h-3.5 w-3.5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
        <p className="text-[11px] text-stone-500">
          Payouts are platform-controlled and run automatically every Monday. Earnings clear 48 hours after service completion and any disputed or refunded bookings are held back. You can't manually trigger a payout, reach out to support if anything looks off.
        </p>
      </div>
    </div>
  );
}
